export interface DatabaseConfig {
  dbName: string;
  version: number;
  storeName: string;
  keyPath?: string;
  autoIncrement?: boolean;
  indexes?: Array<{
    name: string;
    keyPath: string;
    options?: IDBIndexParameters;
  }>;
}

export async function promisifyTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

export async function wrapRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function openDatabase(config: DatabaseConfig): Promise<IDBDatabase> {
  const dbRequest = indexedDB.open(config.dbName, config.version);

  dbRequest.onupgradeneeded = event => {
    const db = (event.target as IDBOpenDBRequest).result;
    if (!db.objectStoreNames.contains(config.storeName)) {
      const storeOptions: IDBObjectStoreParameters = {};

      if (config.keyPath) {
        storeOptions.keyPath = config.keyPath;
      }
      if (config.autoIncrement !== undefined) {
        storeOptions.autoIncrement = config.autoIncrement;
      }

      const store = db.createObjectStore(config.storeName, storeOptions);

      // Create indexes if specified
      if (config.indexes) {
        config.indexes.forEach(index => {
          store.createIndex(index.name, index.keyPath, index.options);
        });
      }
    }
  };

  return wrapRequest<IDBDatabase>(dbRequest);
}

export async function initializeDatabase(config: DatabaseConfig): Promise<IDBDatabase> {
  try {
    return await openDatabase(config);
  } catch (error) {
    try {
      // Attempt recovery by deleting and recreating the database
      const deleteRequest = indexedDB.deleteDatabase(config.dbName);
      await wrapRequest(deleteRequest);
      return await openDatabase(config);
    } catch (recoveryError) {
      throw new Error(`IndexedDB initialization error for ${config.dbName}: ${error}`);
    }
  }
}

export async function addToStore<T>(db: IDBDatabase, storeName: string, data: T): Promise<void> {
  const transaction = db.transaction(storeName, 'readwrite');
  const store = transaction.objectStore(storeName);

  store.add(data);
  await promisifyTransaction(transaction);
}

export async function putToStore<T>(db: IDBDatabase, storeName: string, data: T): Promise<void> {
  const transaction = db.transaction(storeName, 'readwrite');
  const store = transaction.objectStore(storeName);

  store.put(data);
  await promisifyTransaction(transaction);
}

export async function getFromStore<T>(
  db: IDBDatabase,
  storeName: string,
  key: IDBValidKey
): Promise<T | undefined> {
  const transaction = db.transaction(storeName, 'readonly');
  const store = transaction.objectStore(storeName);

  return wrapRequest<T | undefined>(store.get(key));
}

export async function getAllFromStore<T>(db: IDBDatabase, storeName: string): Promise<T[]> {
  const transaction = db.transaction(storeName, 'readonly');
  const store = transaction.objectStore(storeName);

  return wrapRequest<T[]>(store.getAll());
}

export async function deleteFromStore(
  db: IDBDatabase,
  storeName: string,
  key: IDBValidKey
): Promise<void> {
  const transaction = db.transaction(storeName, 'readwrite');
  const store = transaction.objectStore(storeName);

  store.delete(key);
  await promisifyTransaction(transaction);
}

export async function clearStore(db: IDBDatabase, storeName: string): Promise<void> {
  const transaction = db.transaction(storeName, 'readwrite');
  const store = transaction.objectStore(storeName);

  store.clear();
  await promisifyTransaction(transaction);
}

export async function countInStore(db: IDBDatabase, storeName: string): Promise<number> {
  const transaction = db.transaction(storeName, 'readonly');
  const store = transaction.objectStore(storeName);

  return wrapRequest<number>(store.count());
}
